import { CartUpdatedEvent } from '@theme/events';
import { readCartResponse, lockCart } from './cart-feedback.js';

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.applyButton = null;
    this.inputDiscount = null;
    this.removeDiscountButtons = null;
    this.submitHandler = this.submitDiscount.bind(this);
    this.keyHandler = (event) => {
      if (event.key === 'Enter' && event.target === this.inputDiscount) this.submitDiscount(event);
      else if (['Enter', ' '].includes(event.key) && event.target.matches('[data-ref="remove-discount"]')) this.removeDiscount(event);
    };
  }

  connectedCallback() {
    this.applyButton = this.querySelector('[data-ref="apply-discount"]');
    this.inputDiscount = this.querySelector('[data-ref="discount-input"]');
    this.removeDiscountButtons = this.querySelectorAll('[data-ref="remove-discount"]');

    this.applyButton?.addEventListener('click', this.submitHandler);
    this.removeDiscountButtons.forEach((button) => button.addEventListener('click', this.removeDiscount));
    this.addEventListener('keydown', this.keyHandler);
    this.removeDiscountButtons.forEach(button => {
      button.setAttribute('role', 'button');
      button.tabIndex = 0;
      const code = button.closest('[data-discount-code]')?.dataset.discountCode || '';
      button.setAttribute('aria-label', `${document.documentElement.lang.startsWith('fr') ? 'Retirer le code' : 'Remove code'} ${code}`);
    });
  }

  disconnectedCallback() {
    this.applyButton?.removeEventListener('click', this.submitHandler);
    this.removeDiscountButtons.forEach((button) => button.removeEventListener('click', this.removeDiscount));
    this.removeEventListener('keydown', this.keyHandler);
  }

  submitDiscount(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.pending) return;
    const discountCode = this.inputDiscount.value.trim();
    if (discountCode === '') {
      this.setErrorMode(this.dataset.pleaseInsertACode);
    } else {
      this.applyCode(discountCode);
    }
  }

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.pending) return;

    const abortController = new AbortController();

    const badge = event.target.closest('[data-ref="discount-badge"]');
    if (!(badge instanceof HTMLElement)) return;

    const discountCode = badge.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);
    const unlock = lockCart(this.closest('cart-drawer, cart-component') || this);
    if (!unlock) return;
    this.setButtonLoading();

    // On ajoute la section cart-drawer à la requête pour la mettre à jour
    const sections = [];
    if (document.querySelector('cart-drawer')) {
      const section_cart_drawer_id = document.querySelector('cart-drawer').dataset.sectionId;
      sections.push(section_cart_drawer_id);
    }

    try {
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
        signal: abortController.signal,
      };

      const response = await fetch(Theme.routes.cart_update_url, config);
      const data = await readCartResponse(response, this.dataset.errorRemoveDiscount);

      this.updateCart(data);

      this.setButtonLoaded();
    } catch (error) {
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: this.dataset.errorRemoveDiscount } }));
    } finally {
      unlock();
      this.setButtonLoaded();
    }
  };

  async applyCode(discountCode) {
    const unlock = lockCart(this.closest('cart-drawer, cart-component') || this);
    if (!unlock) return;
    const abortController = new AbortController();

    this.setButtonLoading();

    try {
      const existingDiscounts = this.#existingDiscounts();

      if (existingDiscounts.includes(discountCode)) {
        this.setErrorMode(this.dataset.codeAlreadyApplied);
        return;
      }

      // On ajoute la section cart-drawer à la requête pour la mettre à jour
      const sections = [];
      if (document.querySelector('cart-drawer')) {
        const section_cart_drawer_id = document.querySelector('cart-drawer').dataset.sectionId;
        sections.push(section_cart_drawer_id);
      }

      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCode].join(','),
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
        signal: abortController.signal,
      };

      const response = await fetch(Theme.routes.cart_update_url, config);
      const data = await readCartResponse(response, this.dataset.errorApplyDiscount);

      const failedDiscount = data.discount_codes?.find((discount) => discount.code === discountCode && discount.applicable === false);

      if (failedDiscount) {
        this.setErrorMode(this.dataset.codeNotApplicable);
        return;
      }

      this.updateCart(data);

      this.setButtonLoaded();
    } catch (error) {
      if (error.name === 'AbortError') return;

      this.setErrorMode(this.dataset.errorApplyDiscount);
    } finally {
      unlock();
      this.setButtonLoaded();
    }
  }

  updateCart(data) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            source: 'cart-discount',
            itemCount: data.item_count,
            sections: data.sections,
          },
        ),
      );
    }
  }

  #existingDiscounts() {
    const discountCodes = [];
    const discountBadges = this.querySelectorAll('[data-ref="discount-badge"]');

    for (const badge of discountBadges) {
      if (badge instanceof HTMLElement && typeof badge.dataset.discountCode === 'string') {
        discountCodes.push(badge.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  setErrorMode(message) {
    this.setButtonLoaded();

    document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message } }));

    this.inputDiscount.closest('.textfield').classList.add('textfield--error');
    this.inputDiscount.value = '';

    setTimeout(() => {
      this.inputDiscount.closest('.textfield').classList.remove('textfield--error');
    }, 2000);
  }

  setButtonLoading() {
    this.pending = true;
    this.applyButton.disabled = true;
    this.applyButton.classList.add('is-loading');
  }

  setButtonLoaded() {
    this.pending = false;
    this.applyButton.disabled = false;
    this.applyButton.classList.remove('is-loading');
  }
}

if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount);
